import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Bug, BookOpen, RefreshCw, SquareKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { ReadinessBadge } from "@/components/ado/readiness-badge";
import { useMyWorkItems, useWorkItemProjects } from "@/hooks/queries/use-work-items";
import {
  getAdoPatErrorCode,
  ITEM_DETAIL_STATE,
  workItemPagePath,
  type MyWorkItemsFilters,
  type WorkItemSummary,
} from "@/lib/api/work-items";

type KindFilter = "" | "bug" | "story";

const selectClassName =
  "h-9 rounded-md border border-white/[0.08] bg-white/[0.04] px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

/** Group rows by project, keeping the API's order (project, priority, id) within each group. */
function groupByProject(items: readonly WorkItemSummary[]): [string, WorkItemSummary[]][] {
  const groups = new Map<string, WorkItemSummary[]>();
  for (const item of items) {
    const list = groups.get(item.ref.container) ?? [];
    list.push(item);
    groups.set(item.ref.container, list);
  }
  return [...groups.entries()];
}

function KindIcon({ kind }: { kind: WorkItemSummary["kind"] }) {
  return kind === "bug" ? (
    <Bug className="h-4 w-4 shrink-0 text-red-400" aria-hidden="true" />
  ) : (
    <BookOpen className="h-4 w-4 shrink-0 text-sky-400" aria-hidden="true" />
  );
}

function WorkItemRow({ item }: { item: WorkItemSummary }) {
  return (
    <li>
      <Link
        to={workItemPagePath(item.ref)}
        state={ITEM_DETAIL_STATE}
        className="flex flex-col gap-2 rounded-lg px-3 py-2.5 transition-colors hover:bg-white/[0.04] sm:flex-row sm:items-center sm:gap-4"
        data-testid="ado-item-row"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <KindIcon kind={item.kind} />
          <span className="font-mono text-xs text-muted-foreground">#{item.ref.id}</span>
          <span className="truncate text-sm font-medium">{item.title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-7 sm:pl-0">
          <span className="text-xs text-muted-foreground">{item.typeName}</span>
          <span className="rounded border border-white/[0.08] px-1.5 py-0.5 text-[11px] text-muted-foreground">
            {item.state.name}
          </span>
          <ReadinessBadge level="grooming" tier={item.readiness.grooming.tier} />
          <ReadinessBadge level="sprint" tier={item.readiness.sprint.tier} />
        </div>
      </Link>
    </li>
  );
}

/**
 * My Items: the open Azure DevOps bugs and stories assigned to the user
 * (WIQL `@Me` under their own PAT), grouped by project, each with a live
 * grooming badge (gates Epic Request conversion) and a report-only sprint badge.
 */
export function AdoMyItemsPage() {
  const [project, setProject] = useState("");
  const [kind, setKind] = useState<KindFilter>("");

  const filters = useMemo<MyWorkItemsFilters>(
    () => ({
      ...(project ? { projects: [project] } : {}),
      ...(kind ? { kind } : {}),
    }),
    [project, kind]
  );

  const itemsQuery = useMyWorkItems(filters);
  const projectsQuery = useWorkItemProjects();
  const patError = getAdoPatErrorCode(itemsQuery.error) ?? getAdoPatErrorCode(projectsQuery.error);

  const groups = useMemo(() => groupByProject(itemsQuery.data ?? []), [itemsQuery.data]);

  return (
    <div className="container mx-auto space-y-6 px-4 py-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <SquareKanban className="h-7 w-7 text-primary" />
            My ADO Items
          </h1>
          <p className="mt-1 text-muted-foreground">
            Open Azure DevOps bugs and stories assigned to you, with live readiness.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { void itemsQuery.refetch(); }}
          disabled={itemsQuery.isFetching}
          aria-label="Refresh items"
        >
          <RefreshCw className={itemsQuery.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
        </Button>
      </div>

      {patError ? (
        <AdoPatCallout code={patError} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Project
              <select
                aria-label="Project"
                className={selectClassName}
                value={project}
                onChange={(e) => { setProject(e.target.value); }}
              >
                <option value="">All projects</option>
                {(projectsQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Type
              <select
                aria-label="Type"
                className={selectClassName}
                value={kind}
                onChange={(e) => { setKind(e.target.value as KindFilter); }}
              >
                <option value="">Bugs and stories</option>
                <option value="bug">Bugs</option>
                <option value="story">Stories</option>
              </select>
            </label>
          </div>

          {itemsQuery.isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center" role="status">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="sr-only">Loading items</span>
            </div>
          ) : itemsQuery.isError ? (
            <Card className="border-red-500/40 bg-red-500/5" role="alert">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  Could not load your Azure DevOps items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {itemsQuery.error instanceof Error ? itemsQuery.error.message : "Unknown error"}
                </p>
                <Button variant="outline" size="sm" onClick={() => { void itemsQuery.refetch(); }}>
                  Try again
                </Button>
              </CardContent>
            </Card>
          ) : groups.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No open bugs or stories are assigned to you
                {project ? ` in ${project}` : ""}.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {groups.map(([name, items]) => (
                <Card key={name} className="glass-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span>{name}</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {items.length} {items.length === 1 ? "item" : "items"}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="divide-y divide-white/[0.06]">
                      {items.map((item) => (
                        <WorkItemRow key={`${item.ref.container}/${item.ref.id}`} item={item} />
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
